import React, { useState, useEffect, useRef } from 'react';
import {
  FolderOpen, ChevronDown, ChevronUp, CheckCircle,
  User, Briefcase, Send, Camera, AlertTriangle, Share2, ExternalLink, Users, Crown, UserPlus, UserCheck, X, Loader
} from 'lucide-react';
import { projectsAPI } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Badge, EmptyState, Modal, LoadingPage, PageHeader } from '../components/shared';
import toast from 'react-hot-toast';

const MILESTONE_STATUS = {
  pending:     { color: 'var(--text-muted)',  label: 'Pending' },
  in_progress: { color: 'var(--info)',         label: 'In Progress' },
  submitted:   { color: 'var(--warning)',      label: 'Submitted' },
  approved:    { color: 'var(--success)',      label: 'Approved' },
  rejected:    { color: 'var(--error)',        label: 'Needs Revision' },
};

const PROJECT_STATUS = {
  not_started:  { color: 'var(--text-muted)', label: 'Not Started' },
  in_progress:  { color: 'var(--info)',        label: 'In Progress' },
  submitted:    { color: 'var(--warning)',     label: 'Submitted' },
  under_review: { color: 'var(--teal)',        label: 'Under Review' },
  completed:    { color: 'var(--success)',     label: 'Completed' },
};

export default function ProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [inviteModal, setInviteModal] = useState(null); // project
  const [lecturers, setLecturers] = useState([]);
  const [selectedLecturer, setSelectedLecturer] = useState('');
  const [inviting, setInviting] = useState(false);
  const [invitations, setInvitations] = useState({}); // projectId -> invitation
  const [supervisorInvites, setSupervisorInvites] = useState([]); // pending supervision requests for lecturers
  const [respondingInvite, setRespondingInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [submitModal, setSubmitModal] = useState(null);
  const [respondingInv, setRespondingInv] = useState({});

  const reload = () => {
    projectsAPI.getAll().then(r => setProjects(r.data)).catch(() => toast.error('Failed to load projects'));
    if (user?.role === 'student') {
      api.get('/teams/my-invitations').then(r => setInvitations(r.data)).catch(() => {});
    if (user?.role === 'lecturer') {
      projectsAPI.getLecturerInvitations().then(r => setSupervisorInvites(r.data.invitations || [])).catch(() => {});
    }
    }
  };

  useEffect(() => { reload(); setLoading(false); }, []);

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  if (loading) return <LoadingPage />;

  const handleRespondInvite = async (invite, action) => {
    setRespondingInvite(invite.id + action);
    try {
      await projectsAPI.respondToSupervisorInvitation(invite.project_id, invite.id, action);
      toast.success(action === 'accepted' ? 'You are now supervising this project.' : 'Invitation declined.');
      setSupervisorInvites(s => s.filter(i => i.id !== invite.id));
      if (action === 'accepted') reload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to respond.');
    } finally {
      setRespondingInvite(null);
    }
  };

  const handleInvite = async (project) => {
    if (!selectedLecturer) { toast.error('Please select a lecturer.'); return; }
    setInviting(true);
    try {
      await projectsAPI.inviteSupervisor(project.id, selectedLecturer);
      toast.success('Invitation sent!');
      setInviteModal(null);
      setSelectedLecturer('');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send invitation.');
    } finally {
      setInviting(false);
    }
  };

  const openInviteModal = async (project) => {
    setInviteModal(project);
    setSelectedLecturer('');
    try {
      const r = await api.get('/lecturers');
      setLecturers(r.data.lecturers || []);
    } catch { setLecturers([]); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Dashboard"
        title="My Projects"
        subtitle="Track progress, update milestones, and deliver your work"
      />

      {projects.length === 0 && invitations.length === 0 ? (
        <EmptyState icon={FolderOpen} title="No active projects"
          description="Projects appear here after your application is accepted and a sponsor selects you." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Pending supervision invitations for lecturers */}
          {user?.role === 'lecturer' && supervisorInvites.length > 0 && (
            <div style={{ border: '1px solid rgba(42,157,143,0.3)', borderRadius: 12, background: 'rgba(42,157,143,0.04)', padding: 20, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <UserCheck size={16} color="var(--teal)" />
                <span style={{ fontWeight: 600, color: 'var(--teal)', fontSize: '0.95rem' }}>Pending Supervision Requests</span>
                <span style={{ fontSize: '0.72rem', background: 'rgba(42,157,143,0.15)', color: 'var(--teal)', padding: '2px 8px', borderRadius: 999 }}>{supervisorInvites.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {supervisorInvites.map(inv => (
                  <div key={inv.id} style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 2 }}>{inv.project_title}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        <span style={{ color: 'var(--teal)' }}>{inv.student_first} {inv.student_last}</span> has invited you to supervise their project.
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                        Expires: {new Date(inv.expires_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-ghost" style={{ fontSize: '0.8rem', color: 'var(--error)' }} disabled={!!respondingInvite} onClick={() => handleRespondInvite(inv, 'declined')}>
                        Decline
                      </button>
                      <button className="btn btn-primary" style={{ fontSize: '0.8rem' }} disabled={!!respondingInvite} onClick={() => handleRespondInvite(inv, 'accepted')}>
                        {respondingInvite === inv.id + 'accepted' ? 'Accepting...' : 'Accept'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pending team invitations */}
          {user?.role === 'student' && invitations.length > 0 && (
            <div style={{ border: '1px solid rgba(201,168,76,0.3)', borderRadius: 12, background: 'rgba(201,168,76,0.04)', padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <UserPlus size={16} color="var(--gold)" />
                <span style={{ fontWeight: 600, color: 'var(--gold)', fontSize: '0.95rem' }}>Pending Team Invitations</span>
                <span style={{ fontSize: '0.72rem', background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', padding: '2px 8px', borderRadius: 999 }}>{invitations.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {invitations.map(inv => (
                  <div key={inv.team_id} style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 2 }}>{inv.team_name}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        <span style={{ color: 'var(--teal)' }}>{inv.lead_name}</span> invited you to join this team for{' '}
                        <strong style={{ color: 'var(--text-primary)' }}>{inv.topic_title}</strong>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-primary btn-sm" disabled={respondingInv[inv.team_id]}
                        onClick={async () => {
                          setRespondingInv(r => ({ ...r, [inv.team_id]: true }));
                          try {
                            await api.patch(`/teams/${inv.team_id}/invitations/${user.id}`, { action: 'accepted' });
                            toast.success('Invitation accepted!'); reload();
                          } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
                          finally { setRespondingInv(r => ({ ...r, [inv.team_id]: false })); }
                        }}>
                        {respondingInv[inv.team_id] ? '…' : '✓ Accept'}
                      </button>
                      <button className="btn btn-ghost btn-sm" disabled={respondingInv[inv.team_id]}
                        style={{ color: 'var(--error)' }}
                        onClick={async () => {
                          setRespondingInv(r => ({ ...r, [inv.team_id]: true }));
                          try {
                            await api.patch(`/teams/${inv.team_id}/invitations/${user.id}`, { action: 'declined' });
                            toast.success('Invitation declined.'); reload();
                          } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
                          finally { setRespondingInv(r => ({ ...r, [inv.team_id]: false })); }
                        }}>
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {projects.map(project => {
            const statusCfg = PROJECT_STATUS[project.status] || PROJECT_STATUS.not_started;
            const isOpen = expanded[project.id];
            const milestones = Array.isArray(project.milestones) ? project.milestones.filter(m => m && m.id) : [];
            const completedCount = milestones.filter(m => m.status === 'approved').length;
            const totalCount = milestones.length;
            const progress = totalCount ? Math.round((completedCount / totalCount) * 100) : 0;
            const isTeamProject = !!project.team_id;
            const isLead = user?.id === project.lead_student_id;
            const members = project.members || [];

            return (
              <div key={project.id} className="card" style={{ overflow: 'hidden' }}>
                {/* Project header */}
                <div style={{ padding: 24, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleExpand(project.id)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.7rem', fontFamily: 'var(--font-mono)', color: statusCfg.color, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                          ● {statusCfg.label}
                        </span>
                        {project.domain && (
                          <span style={{ fontSize: '0.7rem', color: 'var(--teal)', background: 'rgba(45,212,191,0.1)', padding: '2px 8px', borderRadius: 4 }}>{project.domain}</span>
                        )}
                        {isTeamProject && (
                          <span style={{ fontSize: '0.7rem', color: 'var(--gold)', background: 'rgba(201,168,76,0.1)', padding: '2px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Users size={10} /> Team project
                          </span>
                        )}
                      </div>
                      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>
                        {project.topic_title || project.title}
                      </h2>
                      <div style={{ display: 'flex', gap: 20, fontSize: '0.8rem', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                        {project.lecturer_name && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <User size={13} /> {project.lecturer_name}
                          </span>
                        )}
                        {project.sponsor_name && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <Briefcase size={13} /> Sponsor: {project.sponsor_name}
                          </span>
                        )}
                        {isTeamProject && isLead && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--gold)' }}>
                            <Crown size={13} /> Team Lead
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginLeft: 16 }}>
                      {totalCount > 0 && (
                        <div style={{
                          width: 52, height: 52, borderRadius: '50%',
                          background: `conic-gradient(var(--teal) ${progress * 3.6}deg, rgba(255,255,255,0.08) 0deg)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--ink-mid)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 600, color: 'var(--teal)' }}>
                            {progress}%
                          </div>
                        </div>
                      )}
                      {isOpen ? <ChevronUp size={18} color="var(--text-muted)" /> : <ChevronDown size={18} color="var(--text-muted)" />}
                    </div>
                  </div>

                  {totalCount > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        <span>Progress</span>
                        <span>{completedCount} / {totalCount} milestones approved</span>
                      </div>
                      <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, var(--teal-dim), var(--teal))', borderRadius: 4, transition: 'width 0.6s ease' }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Expanded content */}
                {isOpen && (
                  <div style={{ borderTop: 'var(--border-subtle)', padding: '0 24px 24px' }}>

                    {/* Team Members Panel */}
                    {isTeamProject && members.length > 0 && (
                      <div style={{ paddingTop: 20, marginBottom: 20 }}>
                        <h4 style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
                          Team Members
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {members.map(m => (
                            <div key={m.student_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
                              <div style={{ width: 30, height: 30, borderRadius: '50%', background: m.is_lead ? 'rgba(201,168,76,0.15)' : 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 600, color: m.is_lead ? 'var(--gold)' : 'var(--text-muted)', flexShrink: 0 }}>
                                {m.is_lead ? <Crown size={14} /> : (m.first_name?.[0] || '?')}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '0.875rem', fontWeight: m.is_lead ? 600 : 400, color: m.is_lead ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                                  {m.full_name || `${m.first_name} ${m.last_name}`}
                                  {m.student_id === user?.id && <span style={{ marginLeft: 6, fontSize: '0.72rem', color: 'var(--teal)' }}>(you)</span>}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{m.department}</div>
                              </div>
                              {m.is_lead && (
                                <span style={{ fontSize: '0.72rem', color: 'var(--gold)', background: 'rgba(201,168,76,0.1)', padding: '2px 8px', borderRadius: 4 }}>Team Lead</span>
                              )}
                            </div>
                          ))}
                        </div>
                        {!isLead && (
                          <p style={{ marginTop: 10, fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                            Only the team lead can submit milestones.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Milestones */}
                    <div style={{ paddingTop: isTeamProject && members.length > 0 ? 0 : 20, marginBottom: 16 }}>
                      <h4 style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Milestones</h4>
                      {milestones.length === 0 ? (
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Milestones are being set up. Check back shortly.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {[...milestones].sort((a, b) => (a.order_index || 0) - (b.order_index || 0)).map((m, i) => (
                            <MilestoneRow
                              key={m.id}
                              milestone={m}
                              index={i}
                              projectId={project.id}
                              userRole={user?.role}
                              userId={user?.id}
                              leadStudentId={project.lead_student_id}
                              lecturerId={project.lecturer_id}
                              onUpdate={reload}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {['in_progress', 'not_started'].includes(project.status) && user?.role === 'student' && isLead && (
                      <div style={{ display: 'flex', gap: 12, paddingTop: 16, borderTop: 'var(--border-subtle)', flexWrap: 'wrap' }}>
                        <button className="btn btn-primary" onClick={() => setSubmitModal(project)} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Send size={15} /> Submit Final Project
                        </button>
                        {!project.lecturer_id && (
                          <button className="btn btn-secondary" onClick={() => openInviteModal(project)} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <UserCheck size={15} /> Invite Supervisor
                          </button>
                        )}
                      </div>
                    )}

                    {user?.role === 'student' && isLead && project.public_slug && (
                      <>
                        <ShowcaseToggle project={project} onUpdate={reload} />
                        <ShowcaseProfileEditor project={project} onUpdate={reload} />
                      </>
                    )}

                    {project.is_winner && (
                      <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(201,162,39,0.12)', borderRadius: 8, border: '1px solid rgba(201,162,39,0.3)', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: '1.2rem' }}>🏆</span>
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--gold)' }}>Award Winner</div>
                          {project.award_category && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{project.award_category}</div>}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal open={!!submitModal} onClose={() => setSubmitModal(null)} title="Submit Final Project">
        {/* Invite Supervisor Modal */}
      {inviteModal && (
        <Modal open={!!inviteModal} onClose={() => setInviteModal(null)} title="Invite a Supervisor" size="md">
          <div style={{ padding: '0 4px' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
              Select a lecturer to supervise your project. They will receive an invitation and have 7 days to accept or decline.
            </p>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 8, letterSpacing: '0.05em' }}>SELECT LECTURER</label>
              <select value={selectedLecturer} onChange={e => setSelectedLecturer(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: 'var(--text-primary)', fontSize: '0.875rem' }}>
                <option value="">-- Choose a lecturer --</option>
                {lecturers.map(l => (
                  <option key={l.id} value={l.id}>{l.title ? l.title + ' ' : ''}{l.first_name} {l.last_name} {l.department ? '(' + l.department + ')' : ''}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setInviteModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => handleInvite(inviteModal)} disabled={inviting || !selectedLecturer}>
                {inviting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {submitModal && (
          <SubmitProjectForm
            project={submitModal}
            onSuccess={() => { setSubmitModal(null); reload(); toast.success('Project submitted for judging!'); }}
            onClose={() => setSubmitModal(null)}
          />
        )}
      </Modal>
    </div>
  );
}

function MilestoneRow({ milestone: m, index, projectId, userRole, userId, leadStudentId, lecturerId, onUpdate }) {
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [noteText, setNoteText] = useState(m.submission_notes || '');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const fileRef = useRef();

  const mCfg = MILESTONE_STATUS[m.status] || MILESTONE_STATUS.pending;
  const isLead = userRole === 'student' && userId === leadStudentId;
  const isLecturer = (userRole === 'lecturer' && userId === lecturerId) || userRole === 'admin';
  const canSubmit = isLead && ['pending', 'in_progress', 'rejected'].includes(m.status);
  const canVerify = isLecturer && m.status === 'submitted';

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!noteText.trim()) return toast.error('Please add a note describing your progress');
    setSubmitting(true);
    try {
      if (photoFile) {
        const form = new FormData();
        form.append('photo', photoFile);
        await projectsAPI.addMilestonePhoto(projectId, m.id, form);
      }
      await projectsAPI.updateMilestone(projectId, m.id, { status: 'submitted', submission_notes: noteText });
      toast.success('Milestone submitted for review');
      setShowForm(false); setPhotoFile(null); setPhotoPreview(null);
      onUpdate();
    } catch { toast.error('Submission failed. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const handleVerify = async (approved) => {
    setVerifying(true);
    try {
      await projectsAPI.updateMilestone(projectId, m.id, {
        status: approved ? 'approved' : 'rejected',
        review_notes: approved ? 'Approved by supervisor' : 'Returned for revision — please review and resubmit',
      });
      toast.success(approved ? 'Milestone approved' : 'Milestone returned for revision');
      onUpdate();
    } catch { toast.error('Action failed'); }
    finally { setVerifying(false); }
  };

  return (
    <div style={{
      borderRadius: 10,
      border: `1px solid ${m.status === 'approved' ? 'rgba(34,197,94,0.25)' : m.status === 'rejected' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.07)'}`,
      background: m.status === 'approved' ? 'rgba(34,197,94,0.04)' : 'rgba(255,255,255,0.02)',
      overflow: 'hidden',
    }}>
      <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, background: m.status === 'approved' ? 'var(--success)' : m.status === 'submitted' ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 600, color: m.status === 'approved' ? '#fff' : m.status === 'submitted' ? 'var(--warning)' : 'var(--text-muted)' }}>
            {m.status === 'approved' ? <CheckCircle size={14} /> : index + 1}
          </div>
          <div>
            <div style={{ fontWeight: 500, fontSize: '0.9rem', color: 'var(--text-primary)' }}>{m.title}</div>
            {m.submitted_at && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                Submitted {new Date(m.submitted_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '0.7rem', color: mCfg.color, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{mCfg.label}</span>
          {canSubmit && (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.75rem', color: 'var(--teal)', padding: '4px 10px' }}
              onClick={() => setShowForm(f => !f)}>
              {showForm ? 'Cancel' : 'Update'}
            </button>
          )}
          {canVerify && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-sm btn-teal" disabled={verifying} onClick={() => handleVerify(true)} style={{ fontSize: '0.75rem', padding: '4px 10px' }}>Approve</button>
              <button className="btn btn-sm btn-danger" disabled={verifying} onClick={() => handleVerify(false)} style={{ fontSize: '0.75rem', padding: '4px 10px' }}>Revise</button>
            </div>
          )}
        </div>
      </div>

      {m.submission_notes && !showForm && (
        <div style={{ padding: '0 16px 12px', paddingLeft: 54 }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{m.submission_notes}</div>
          {m.submission_url && (
            <div style={{ marginTop: 8 }}>
              <img src={m.submission_url} alt="Milestone photo"
                style={{ maxWidth: 200, maxHeight: 140, borderRadius: 6, objectFit: 'cover', cursor: 'pointer' }}
                onClick={() => window.open(m.submission_url, '_blank')}
                onError={e => { e.target.style.display = 'none'; }} />
            </div>
          )}
        </div>
      )}

      {m.review_notes && m.status === 'rejected' && (
        <div style={{ margin: '0 16px 12px', padding: '8px 12px', background: 'rgba(239,68,68,0.08)', borderRadius: 6, fontSize: '0.8rem', color: 'var(--error)', display: 'flex', gap: 8 }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{m.review_notes}</span>
        </div>
      )}

      {showForm && (
        <div style={{ padding: '12px 16px 16px', paddingLeft: 54, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label" style={{ fontSize: '0.8rem' }}>Progress note *</label>
            <textarea className="form-textarea" rows={3}
              placeholder="Describe what you have done for this milestone..."
              value={noteText} onChange={e => setNoteText(e.target.value)} style={{ fontSize: '0.85rem' }} />
          </div>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="form-label" style={{ fontSize: '0.8rem' }}>Photo (optional)</label>
            <input type="file" accept="image/jpeg,image/png,image/webp" ref={fileRef} style={{ display: 'none' }} onChange={handlePhotoChange} />
            {photoPreview ? (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <img src={photoPreview} alt="Preview" style={{ width: 100, height: 70, objectFit: 'cover', borderRadius: 6 }} />
                <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.75rem' }} onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}>Remove</button>
              </div>
            ) : (
              <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => fileRef.current?.click()}>
                <Camera size={14} /> Attach photo
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" style={{ fontSize: '0.8rem' }} onClick={handleSubmit} disabled={submitting || !noteText.trim()}>
              {submitting ? 'Submitting…' : 'Submit for Review'}
            </button>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.8rem' }}
              onClick={() => { setShowForm(false); setNoteText(m.submission_notes || ''); setPhotoFile(null); setPhotoPreview(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ShowcaseProfileEditor({ project, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [abstract, setAbstract] = useState(project.abstract || '');
  const [tools, setTools] = useState((project.tools_used || []).join(', '));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const toolsArray = tools.split(',').map(t => t.trim()).filter(Boolean);
      await projectsAPI.updateShowcaseProfile(project.id, { abstract, tools_used: toolsArray });
      toast.success('Showcase profile updated');
      setOpen(false); onUpdate();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.78rem', color: 'var(--teal)' }} onClick={() => setOpen(o => !o)}>
        {open ? 'Cancel' : 'Edit showcase profile'}
      </button>
      {open && (
        <div style={{ marginTop: 12, padding: 16, background: 'rgba(255,255,255,0.02)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="form-label" style={{ fontSize: '0.8rem' }}>Project abstract</label>
            <textarea className="form-textarea" rows={4}
              placeholder="A 200-300 word summary of the problem you solved, your methodology, and key findings."
              value={abstract} onChange={e => setAbstract(e.target.value)} />
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>{abstract.length} characters</div>
          </div>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="form-label" style={{ fontSize: '0.8rem' }}>Technologies & tools used</label>
            <input className="form-input" placeholder="e.g. MATLAB, KiCad, Python, Arduino (comma-separated)"
              value={tools} onChange={e => setTools(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </div>
      )}
    </div>
  );
}

function ShowcaseToggle({ project, onUpdate }) {
  const [enabled, setEnabled] = useState(project.showcase_enabled || false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const showcaseUrl = `${window.location.origin}/showcase/${project.public_slug}`;

  const handleToggle = async () => {
    setSaving(true);
    try { const next = !enabled; await projectsAPI.toggleShowcase(project.id, next); setEnabled(next); onUpdate(); }
    catch { }
    finally { setSaving(false); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(showcaseUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div style={{ marginTop: 16, padding: '14px 16px', background: 'rgba(42,157,143,0.06)', borderRadius: 10, border: '1px solid rgba(42,157,143,0.15)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <div>
          <div style={{ fontWeight: 500, fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: 3 }}>Public showcase page</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {enabled ? 'Your project is publicly visible and shareable.' : 'Enable to share your project with sponsors and on LinkedIn.'}
          </div>
        </div>
        <button onClick={handleToggle} disabled={saving} style={{ width: 48, height: 26, borderRadius: 13, flexShrink: 0, background: enabled ? 'var(--teal)' : 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
          <span style={{ position: 'absolute', top: 3, left: enabled ? 26 : 4, width: 20, height: 20, borderRadius: 10, background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }} />
        </button>
      </div>
      {enabled && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ flex: 1, padding: '7px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: 6, fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {showcaseUrl}
          </div>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }} onClick={handleCopy}>
            <Share2 size={13} /> {copied ? 'Copied!' : 'Copy'}
          </button>
          <a href={showcaseUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm"
            style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, textDecoration: 'none' }}>
            <ExternalLink size={13} /> View
          </a>
        </div>
      )}
    </div>
  );
}

function SubmitProjectForm({ project, onSuccess, onClose }) {
  const [summary, setSummary] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!summary.trim()) return toast.error('Please provide a project summary');
    if (summary.trim().length < 50) return toast.error('Summary must be at least 50 characters');
    setSubmitting(true);
    try { await projectsAPI.submit(project.id, { summary }, []); onSuccess(); }
    catch { toast.error('Submission failed. Please try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 20, fontSize: '0.9rem', lineHeight: 1.6 }}>
        Submit <strong style={{ color: 'var(--text-primary)' }}>{project.topic_title || project.title}</strong> for evaluation.
        Make sure all milestones are updated before submitting. This action cannot be undone.
      </p>
      <div className="form-group" style={{ marginBottom: 20 }}>
        <label className="form-label">Public Summary *</label>
        <textarea className="form-textarea" rows={5}
          placeholder="Describe your project outcomes, methodology, and key findings (min. 50 characters)..."
          value={summary} onChange={e => setSummary(e.target.value)} />
        <div style={{ fontSize: '0.75rem', color: summary.length < 50 ? 'var(--text-muted)' : 'var(--success)', marginTop: 4 }}>
          {summary.length} / 50 characters minimum
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || summary.trim().length < 50}>
          {submitting ? 'Submitting…' : 'Submit for Judging'}
        </button>
      </div>
    </div>
  );
}
